import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ChefHat, Eye, EyeOff, User, Trash2, ChevronDown, Download, Share2, PlusSquare } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [showSavedAccounts, setShowSavedAccounts] = useState(false);

  // PWA install state
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIosInstructions, setShowIosInstructions] = useState(false);

  const isIos = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);

  // Detect if app is already installed (running standalone) and capture install prompt
  useEffect(() => {
    const checkStandalone = () =>
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(checkStandalone());

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const handleInstalled = () => {
      setInstallPrompt(null);
      setIsStandalone(true);
      toast.success("SupplierFlow installed on your device");
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === "accepted") {
        setInstallPrompt(null);
      }
      return;
    }
    if (isIos) {
      setShowIosInstructions(true);
      return;
    }
    // Fallback for browsers that don't fire beforeinstallprompt (e.g. Firefox, Safari desktop)
    setShowIosInstructions(true);
  };

  // Load saved accounts on mount
  useEffect(() => {
    const accounts = JSON.parse(localStorage.getItem("savedAccounts") || "[]");
    setSavedAccounts(accounts);
    
    // Auto-fill with last used account
    const lastUsed = localStorage.getItem("lastUsedAccount");
    if (lastUsed) {
      const account = accounts.find(a => a.email === lastUsed);
      if (account) {
        setEmail(account.email);
        setPassword(account.password);
      }
    } else if (accounts.length > 0) {
      // Use first saved account
      setEmail(accounts[0].email);
      setPassword(accounts[0].password);
    }
  }, []);

  const selectAccount = (account) => {
    setEmail(account.email);
    setPassword(account.password);
    setShowSavedAccounts(false);
  };

  const removeAccount = (e, emailToRemove) => {
    e.stopPropagation();
    const updated = savedAccounts.filter(a => a.email !== emailToRemove);
    setSavedAccounts(updated);
    localStorage.setItem("savedAccounts", JSON.stringify(updated));
    if (email === emailToRemove) {
      setEmail("");
      setPassword("");
    }
    toast.success("Account removed");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(email, password, rememberMe);
      
      // Save account if remember me is checked
      if (rememberMe) {
        const accounts = JSON.parse(localStorage.getItem("savedAccounts") || "[]");
        const existingIndex = accounts.findIndex(a => a.email === email);
        
        if (existingIndex >= 0) {
          // Update existing account
          accounts[existingIndex] = { email, password };
        } else {
          // Add new account
          accounts.push({ email, password });
        }
        
        localStorage.setItem("savedAccounts", JSON.stringify(accounts));
        localStorage.setItem("lastUsedAccount", email);
        setSavedAccounts(accounts);
      }
      
      toast.success("Welcome back!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.5)), url('https://images.unsplash.com/photo-1584910799193-44ff686a1b7b?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <Card className="w-full max-w-md animate-fade-in shadow-2xl border-0" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-kale-900 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <ChefHat className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-kale-900">SupplierFlow</CardTitle>
          <CardDescription className="text-stone-600">
            Inventory Management System
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Saved Accounts Dropdown */}
            {savedAccounts.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-stone-700">Saved Accounts</Label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowSavedAccounts(!showSavedAccounts)}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-left hover:bg-stone-100 transition-colors"
                    data-testid="saved-accounts-dropdown"
                  >
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-stone-500" />
                      <span className="text-sm text-stone-700">
                        {savedAccounts.length} saved account{savedAccounts.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-stone-500 transition-transform ${showSavedAccounts ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showSavedAccounts && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg z-10 overflow-hidden">
                      {savedAccounts.map((account) => (
                        <div
                          key={account.email}
                          onClick={() => selectAccount(account)}
                          className={`flex items-center justify-between px-3 py-2.5 hover:bg-stone-50 cursor-pointer transition-colors ${email === account.email ? 'bg-kale-50' : ''}`}
                          data-testid={`saved-account-${account.email}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${email === account.email ? 'bg-kale-600 text-white' : 'bg-stone-200 text-stone-600'}`}>
                              {account.email.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm text-stone-700">{account.email}</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => removeAccount(e, account.email)}
                            className="p-1 text-stone-400 hover:text-red-500 transition-colors"
                            title="Remove account"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-stone-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="input-mobile"
                required
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-stone-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="input-mobile pr-10"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-700 transition-colors"
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="remember-me" 
                checked={rememberMe}
                onCheckedChange={setRememberMe}
                data-testid="remember-me-checkbox"
              />
              <Label 
                htmlFor="remember-me" 
                className="text-sm text-stone-600 cursor-pointer select-none"
              >
                Remember me
              </Label>
            </div>
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={isLoading}
              data-testid="login-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>

            {/* Install as Web App */}
            {!isStandalone && (
              <>
                <div className="relative py-1">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-stone-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-stone-500">or</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleInstallClick}
                  className="w-full border-kale-300 text-kale-800 hover:bg-kale-50"
                  data-testid="install-app-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Install App on your phone
                </Button>
                <p className="text-[11px] text-center text-stone-500 -mt-1">
                  Works offline shell • Full-screen • Home-screen icon
                </p>
              </>
            )}
          </form>
        </CardContent>
      </Card>

      {/* iOS / fallback install instructions */}
      <Dialog open={showIosInstructions} onOpenChange={setShowIosInstructions}>
        <DialogContent className="max-w-sm" data-testid="install-instructions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-kale-700" />
              Install SupplierFlow
            </DialogTitle>
            <DialogDescription>
              {isIos
                ? "Add SupplierFlow to your iPhone home screen in 3 steps."
                : "Add SupplierFlow to your home screen from your browser menu."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {isIos ? (
              <>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-kale-100 text-kale-800 flex items-center justify-center text-sm font-semibold shrink-0">1</div>
                  <div className="text-sm text-stone-700">
                    Tap the <span className="inline-flex items-center gap-1 font-medium"><Share2 className="w-4 h-4 inline" /> Share</span> button in Safari (bottom of the screen).
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-kale-100 text-kale-800 flex items-center justify-center text-sm font-semibold shrink-0">2</div>
                  <div className="text-sm text-stone-700">
                    Scroll and choose <span className="inline-flex items-center gap-1 font-medium"><PlusSquare className="w-4 h-4 inline" /> Add to Home Screen</span>.
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-kale-100 text-kale-800 flex items-center justify-center text-sm font-semibold shrink-0">3</div>
                  <div className="text-sm text-stone-700">
                    Tap <span className="font-medium">Add</span> in the top-right. SupplierFlow will appear on your home screen like a native app.
                  </div>
                </div>
                <p className="text-xs text-stone-500 pt-2">
                  Tip: use Safari on iPhone — Chrome on iOS doesn&apos;t support Add to Home Screen for web apps.
                </p>
              </>
            ) : (
              <>
                <div className="text-sm text-stone-700">
                  Open your browser menu <span className="font-medium">(⋮)</span> and choose
                  <span className="font-medium"> &ldquo;Install app&rdquo;</span> or
                  <span className="font-medium"> &ldquo;Add to Home screen&rdquo;</span>.
                </div>
                <p className="text-xs text-stone-500">
                  On Chrome / Edge / Samsung Internet the button will show up automatically. If you don&apos;t see it, refresh the page and try again.
                </p>
              </>
            )}
          </div>

          <DialogFooter>
            <Button className="w-full" onClick={() => setShowIosInstructions(false)} data-testid="install-instructions-close">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
